import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TablePagination,
  TextField,
} from '@mui/material';
import type {
  PublishingChannelView,
  PublishingPageSize,
  PublishingHistory,
  PublishingOverview,
  PublishingPreview,
  DeliveryView,
} from '../../../api/src/telegram/publishing.types';
import { PUBLISHING_PAGE_SIZES } from '../../../api/src/telegram/publishing.types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../api/client';
import { PublishingChannelTable, PublishingChannelDrawer } from './TelegramPublishingChannels';
import { Stack, Typography, Box } from '../components/mui-system';
import { EditorOnly, useCanEdit } from '../components/EditorOnly';
import { PublishingChannelForm } from './TelegramPublishingForm';
import { PublishingSourceSettings } from './TelegramPublishingSource';
import { PublishingMessageSettingsForm } from './TelegramPublishingMessages';

const message = (e: unknown) => (e instanceof Error ? e.message : 'Не удалось выполнить действие');
const date = (value: number | null, zone?: string) =>
  value === null ? '—' : new Date(value).toLocaleString('ru-RU', { timeZone: zone });
const statusNames: Record<DeliveryView['status'], string> = {
  preparing: 'Подготовка',
  sending: 'Отправка',
  sent: 'Опубликовано',
  failed: 'Ошибка',
  uncertain: 'Нужна проверка',
  cancelled: 'Отменено',
};
export function PublishingWorkspace({ workspaceId: w }: { workspaceId: string }) {
  const canRun = useCanEdit('telegram.run');
  const canSettings = useCanEdit('telegram.settings');
  const canPublish = useCanEdit('telegram.publish');
  const [query, setQuery] = useState<{ page: number; pageSize: PublishingPageSize }>({
    page: 1,
    pageSize: PUBLISHING_PAGE_SIZES[0],
  });
  const [overview, setOverview] = useState<{
    query: typeof query;
    data: PublishingOverview;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const data = overview?.data;
  const pageData = overview?.query === query ? data : null;
  const selected = pageData?.channels.find((c) => c.id === selectedId);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<PublishingChannelView | 'new' | null>(null),
    [botDialog, setBotDialog] = useState(false),
    [token, setToken] = useState('');
  const [preview, setPreview] = useState<{
    channel: PublishingChannelView;
    data: PublishingPreview;
  } | null>(null);
  const [history, setHistory] = useState<{
    channel: PublishingChannelView;
    data: PublishingHistory;
  } | null>(null);
  const [skipped, setSkipped] = useState<Array<{ personId: number; reason: string }> | null>(null);
  const [resolving, setResolving] = useState<DeliveryView | null>(null),
    [messageIds, setMessageIds] = useState('');
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    try {
      const next = await api.publishing(w, query.page, query.pageSize);
      if (current !== requestId.current) return;
      const appliedQuery = next.page === query.page ? query : { ...query, page: next.page };
      setOverview({ query: appliedQuery, data: next });
      setQuery(appliedQuery);
      setLoadError('');
    } catch (e) {
      if (current === requestId.current) setLoadError(message(e));
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [w, query]);
  useEffect(() => {
    let inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        await load();
      } finally {
        inFlight = false;
      }
    };
    void poll();
    const timer = setInterval(() => void poll(), 15000);
    return () => {
      requestId.current++;
      clearInterval(timer);
    };
  }, [load]);
  function changePage(page: number, pageSize = query.pageSize) {
    requestId.current++;
    setSelectedId(null);
    setLoadError('');
    setQuery({ page, pageSize });
  }
  async function action(run: () => Promise<void>) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await run();
      await load();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function showHistory(c: PublishingChannelView, page = 1) {
    setHistory({ channel: c, data: await api.publishingHistory(w, c.id, page) });
  }
  return (
    <Stack gap={2}>
      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}
      {loadError && (
        <Alert
          severity="error"
          action={
            <Button disabled={loading} onClick={() => void load()}>
              Повторить загрузку
            </Button>
          }
        >
          {loadError}
          {pageData ? ' Показаны последние загруженные данные.' : ''}
        </Alert>
      )}
      {data && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack gap={0.5} sx={{ minWidth: 0 }}>
              <Typography>Настройки раздела</Typography>
              <Typography
                variant="body2"
                color={data.integration.error ? 'error.main' : 'text.secondary'}
                sx={{ overflowWrap: 'anywhere' }}
              >
                {data.integration.error
                  ? `Ошибка API: ${data.integration.error}`
                  : data.integration.checkedAt
                    ? `API: ${data.integration.sourceUrl}`
                    : 'API ещё не проверен'}{' '}
                · Ботов: {data.bots.length}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              <EditorOnly permissions="telegram.settings">
                <Button
                  onClick={() => {
                    setToken('');
                    setBotDialog(true);
                  }}
                  disabled={busy}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Подключить бота
                </Button>
              </EditorOnly>
              <PublishingSourceSettings
                workspaceId={w}
                value={data.integration}
                canEdit={canSettings}
                onChanged={async () => {
                  requestId.current++;
                  setPreview(null);
                  await load();
                }}
              />
              {data.messageSettings && (
                <PublishingMessageSettingsForm
                  key={w}
                  workspaceId={w}
                  value={data.messageSettings}
                  canEdit={canSettings}
                  onChanged={async () => {
                    setPreview(null);
                    await load();
                  }}
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        <EditorOnly permissions={['telegram.create', 'telegram.schedule']}>
          <Button variant="contained" disabled={busy || !data} onClick={() => setEditing('new')}>
            Добавить канал
          </Button>
        </EditorOnly>
        <Button disabled={busy || loading} onClick={() => void load()}>
          Обновить
        </Button>
        {data && <Typography color="text.secondary">Каналов: {data.total}</Typography>}
      </Stack>
      {!pageData && loading && <CircularProgress aria-label="Загрузка каналов" />}
      {pageData?.total === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6">Добавьте первый канал</Typography>
            <Typography color="text.secondary">
              Подключите бота, укажите город и задайте время публикаций. Новые каналы создаются с
              выключенным расписанием.
            </Typography>
          </CardContent>
        </Card>
      )}
      {data && data.total > 0 && (
        <Card aria-busy={loading}>
          {pageData && (
            <PublishingChannelTable channels={pageData.channels} onOpen={setSelectedId} />
          )}
          <TablePagination
            component="div"
            count={data.total}
            page={query.page - 1}
            rowsPerPage={query.pageSize}
            rowsPerPageOptions={[...PUBLISHING_PAGE_SIZES]}
            disabled={busy}
            onPageChange={(_, page) => changePage(page + 1)}
            onRowsPerPageChange={(e) => changePage(1, Number(e.target.value) as PublishingPageSize)}
            labelRowsPerPage="На странице"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
            getItemAriaLabel={(type) =>
              type === 'next' ? 'Следующая страница' : 'Предыдущая страница'
            }
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                px: 1,
              },
              '& .MuiTablePagination-spacer': { display: 'none' },
            }}
          />
        </Card>
      )}
      {selected && (
        <PublishingChannelDrawer
          channel={selected}
          busy={busy || loading}
          error={error || loadError}
          notice={notice}
          onClose={() => setSelectedId(null)}
          onEnabled={(enabled) =>
            void action(async () => {
              await api.publishingEnabled(w, selected.id, selected.revision, enabled);
            })
          }
          onEdit={() => setEditing(selected)}
          onRefresh={() =>
            void action(async () => {
              await api.publishingRefresh(w, selected.id);
              setNotice('Обновление каталога запрошено. Статус обновится автоматически.');
            })
          }
          onPreview={() =>
            void action(async () =>
              setPreview({ channel: selected, data: await api.publishingPreview(w, selected.id) }),
            )
          }
          onHistory={() => void action(() => showHistory(selected))}
          onSkipped={() =>
            void action(async () => setSkipped(await api.publishingSkipped(w, selected.id)))
          }
        />
      )}
      <Dialog
        open={botDialog}
        onClose={() => {
          if (!busy) {
            setBotDialog(false);
            setToken('');
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Подключение бота</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <Typography>
              Добавьте бота администратором нужных каналов с правом публикации. Один бот может
              обслуживать несколько каналов.
            </Typography>
            <TextField
              label="Токен бота"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="new-password"
              fullWidth
              helperText="Повторный ввод токена того же бота обновит подключение."
            />
            {data?.bots.map((b) => (
              <Typography key={b.id}>
                {b.name} · @{b.username}
              </Typography>
            ))}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => {
              setToken('');
              setBotDialog(false);
            }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            disabled={busy || !token.trim()}
            onClick={() =>
              void action(async () => {
                await api.publishingBot(w, token);
                setToken('');
                setBotDialog(false);
                setNotice('Бот проверен и подключён.');
              })
            }
          >
            {busy ? 'Проверка…' : 'Проверить и сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
      {editing && data && (
        <PublishingChannelForm
          key={editing === 'new' ? 'new' : editing.id}
          value={editing === 'new' ? null : editing}
          workspaceId={w}
          integration={data.integration}
          bots={data?.bots ?? []}
          busy={busy}
          error={error}
          onClose={() => setEditing(null)}
          onSave={(input) =>
            void action(async () => {
              await api.publishingSave(w, input, editing === 'new' ? undefined : editing.id);
              setEditing(null);
              setNotice('Канал сохранён. Часовой пояс определён по городу.');
            })
          }
        />
      )}
      <Dialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="md">
        <DialogTitle>Предпросмотр · {preview?.channel.title}</DialogTitle>
        <DialogContent>
          {preview?.data.reason ? (
            <Alert severity="info">{preview.data.reason}</Alert>
          ) : (
            <Stack gap={2}>
              <Stack direction="row" gap={1} sx={{ overflowX: 'auto' }}>
                {(canRun ? preview?.data.profile?.mediaIds : [])?.map((id) => (
                  <Box
                    key={id}
                    component="img"
                    src={`/api/workspaces/${w}/telegram/publishing/channels/${preview!.channel.id}/preview/photo/${id}?personId=${preview!.data.profile!.id}`}
                    alt={`Фото анкеты ${preview!.data.profile!.name}`}
                    sx={{
                      width: 'min(30%, 240px)',
                      minWidth: 120,
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                ))}
              </Stack>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{preview?.data.caption}</Typography>
              <Typography color="text.secondary">
                Предпросмотр не отправляет сообщение. К моменту публикации следующая анкета может
                измениться.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!history} onClose={() => setHistory(null)} fullWidth maxWidth="md">
        <DialogTitle>История · {history?.channel.title}</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            {history?.data.days.length ? (
              <Typography color="text.secondary">
                Опубликовано по дням:{' '}
                {history.data.days.map((d) => `${d.day}: ${d.sent}`).join(' · ')}
              </Typography>
            ) : null}
            {history?.data.items.length === 0 && (
              <Alert severity="info">Публикаций пока нет.</Alert>
            )}
            {history?.data.items.map((row) => (
              <Card key={row.id} variant="outlined">
                <CardContent>
                  <Stack gap={1}>
                    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                      <Chip
                        label={statusNames[row.status]}
                        color={
                          row.status === 'sent'
                            ? 'success'
                            : row.status === 'uncertain'
                              ? 'warning'
                              : 'default'
                        }
                      />
                      <Typography>
                        {row.profile.name}, {row.profile.age} ·{' '}
                        {date(row.createdAt, history.channel.timeZone)}
                      </Typography>
                    </Stack>
                    <Link href={row.profile.url} target="_blank" rel="noopener noreferrer">
                      Анкета #{row.personId}
                    </Link>
                    {row.postUrl && (
                      <Link href={row.postUrl} target="_blank" rel="noopener noreferrer">
                        Открыть публикацию
                      </Link>
                    )}
                    {row.error && <Typography color="error">{row.error}</Typography>}
                    {row.status === 'uncertain' && canPublish && (
                      <Button
                        disabled={busy}
                        onClick={() => {
                          setMessageIds('');
                          setResolving(row);
                        }}
                      >
                        Указать результат проверки
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Stack direction="row" gap={1}>
              <Button
                disabled={busy || !history || history.data.page === 1}
                onClick={() =>
                  void action(() => showHistory(history!.channel, history!.data.page - 1))
                }
              >
                Назад
              </Button>
              <Button
                disabled={busy || !history || history.data.page * 30 >= history.data.total}
                onClick={() =>
                  void action(() => showHistory(history!.channel, history!.data.page + 1))
                }
              >
                Далее
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistory(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!resolving}
        onClose={() => !busy && setResolving(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Результат отправки</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>
              Проверьте канал. Если пост опубликован, укажите ID сообщений каждого фото из ссылок
              Telegram, через запятую.
            </Typography>
            <TextField
              label="ID сообщений"
              value={messageIds}
              onChange={(e) => setMessageIds(e.target.value)}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setResolving(null)}>
            Отмена
          </Button>
          {(['not_sent', 'sent'] as const).map((outcome) => (
            <Button
              key={outcome}
              disabled={busy || (outcome === 'sent' && !messageIds.trim())}
              onClick={() =>
                void action(async () => {
                  await api.publishingResolve(
                    w,
                    resolving!.channelId,
                    resolving!.id,
                    outcome,
                    outcome === 'sent'
                      ? messageIds
                          .split(/[\s,]+/)
                          .filter(Boolean)
                          .map(Number)
                      : undefined,
                  );
                  setResolving(null);
                  await showHistory(history!.channel, history!.data.page);
                })
              }
            >
              {outcome === 'sent' ? 'Пост опубликован' : 'Поста нет в канале'}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
      <Dialog open={skipped !== null} onClose={() => setSkipped(null)} fullWidth maxWidth="sm">
        <DialogTitle>Пропущенные анкеты · последние 100</DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            {skipped?.map((r) => (
              <Typography key={r.personId}>
                #{r.personId}: {r.reason}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipped(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
